/**
 * Scene 4: North vs South
 * "A Tale of Two Nigerias"
 */

import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { AnimatedCounter, AnimatedPercentage } from '../AnimatedCounter';
import { NigeriaMap } from '../NigeriaMap';
import { CompassIcon } from '../../icons/StoryIcons';
import type { StateGeoFeature } from '../../../types';
import type { StoryInsights } from '../../../hooks/useStoryData';

gsap.registerPlugin(ScrollTrigger);

interface Scene4Props {
  insights: StoryInsights;
  features: StateGeoFeature[];
  isActive: boolean;
}

export function Scene4NorthSouth({ insights, features, isActive }: Scene4Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const northRef = useRef<HTMLDivElement>(null);
  const southRef = useRef<HTMLDivElement>(null);
  const comparisonRef = useRef<HTMLDivElement>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  
  const totalAllocation = insights.northTotal + insights.southTotal;
  const northPercentage = (insights.northTotal / totalAllocation) * 100;
  const southPercentage = (insights.southTotal / totalAllocation) * 100;
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    const ctx = gsap.context(() => {
      // Only set initial hidden state if not active
      if (!isActive) {
        gsap.set(titleRef.current, { opacity: 0, y: 50 });
        gsap.set(northRef.current, { opacity: 0, x: -50 });
        gsap.set(southRef.current, { opacity: 0, x: 50 });
        gsap.set(comparisonRef.current, { opacity: 0, y: 30 });
        gsap.set(mapContainerRef.current, { opacity: 0, scale: 0.9 });
      }
      
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top center',
          end: 'center center',
          toggleActions: 'play none none reverse',
        },
      });
      
      tl.to(titleRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out',
      })
      .to(mapContainerRef.current, {
        opacity: 1,
        scale: 1,
        duration: 1,
        ease: 'power3.out',
      }, '-=0.4')
      .to(northRef.current, {
        opacity: 1,
        x: 0,
        duration: 0.8,
        ease: 'power3.out',
      }, '-=0.6')
      .to(southRef.current, {
        opacity: 1,
        x: 0,
        duration: 0.8,
        ease: 'power3.out',
      }, '-=0.6')
      .to(comparisonRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out',
      }, '-=0.4');
    }, containerRef);
    
    return () => ctx.revert();
  }, []);
  
  // Force visibility when scene becomes active (for auto-scroll)
  useEffect(() => {
    if (isActive) {
      gsap.to(titleRef.current, { opacity: 1, y: 0, duration: 0.5 });
      gsap.to(mapContainerRef.current, { opacity: 1, scale: 1, duration: 0.5 });
      gsap.to(northRef.current, { opacity: 1, x: 0, duration: 0.5 });
      gsap.to(southRef.current, { opacity: 1, x: 0, duration: 0.5 });
      gsap.to(comparisonRef.current, { opacity: 1, y: 0, duration: 0.5 });
    }
  }, [isActive]);
  
  return (
    <section
      ref={containerRef}
      className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden px-4 py-20"
    >
      {/* Gradient background split */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-cyan-500/5 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-amber-500/5 to-transparent" />
      </div>
      
      {/* Content */}
      <div className="relative z-10 max-w-6xl w-full">
        {/* Title */}
        <div ref={titleRef} className="text-center mb-12 group">
          <div className="flex items-center justify-center gap-4 mb-4">
            <CompassIcon size={40} className="text-surface-300 transition-all duration-500 group-hover:text-accent-400 group-hover:scale-110 group-hover:rotate-45" />
            <h2 className="text-3xl md:text-5xl font-display font-bold text-white transition-all duration-300 group-hover:tracking-wide">
              A Tale of Two Nigerias
            </h2>
          </div>
          <p className="text-xl md:text-2xl text-surface-300">
            Regional distribution of federal allocations
          </p>
        </div>
        
        {/* Main layout */}
        <div className="grid lg:grid-cols-3 gap-4 md:gap-8 items-center">
          {/* Map - shown first on mobile */}
          <div ref={mapContainerRef} className="relative order-first lg:order-none mb-4 lg:mb-0 flex items-center justify-center">
            <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/20 via-transparent to-amber-500/20 rounded-full blur-3xl" />
            
            {/* Dividing line */}
            <div className="absolute left-0 right-0 top-[45%] h-0.5 bg-gradient-to-r from-transparent via-surface-500 to-transparent z-10" />
            
            <div className="relative z-0 w-[220px] h-[220px] sm:w-[280px] sm:h-[280px] md:w-[350px] md:h-[350px]">
              {/* North highlighted map */}
              <div className="absolute inset-0">
                <NigeriaMap
                  features={features}
                  width={350}
                  height={350}
                  regionHighlight="north"
                  highlightColor="#22d3ee"
                  defaultColor="transparent"
                  className="w-full h-full opacity-60"
                />
              </div>
              
              {/* South highlighted map */}
              <div className="absolute inset-0">
                <NigeriaMap
                  features={features}
                  width={350}
                  height={350}
                  regionHighlight="south"
                  highlightColor="#f59e0b"
                  defaultColor="transparent"
                  className="w-full h-full opacity-60"
                />
              </div>
              
              {/* Base map outline */}
              <NigeriaMap
                features={features}
                width={350}
                height={350}
                defaultColor="#1e293b"
                className="w-full h-full"
              />
            </div>
          </div>
          
          {/* North & South stats - side by side on mobile, separate columns on desktop */}
          <div className="grid grid-cols-2 lg:grid-cols-1 gap-3 md:gap-4 col-span-2 lg:col-span-1 lg:order-first">
            {/* North stats */}
            <div ref={northRef} className="space-y-2 md:space-y-4">
              <div className="p-3 sm:p-4 md:p-6 bg-cyan-500/10 rounded-xl md:rounded-2xl border border-cyan-500/30">
                <div className="flex items-center gap-1.5 md:gap-2 mb-2 md:mb-4">
                  <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-cyan-400" />
                  <span className="text-cyan-400 font-medium uppercase tracking-wide text-xs md:text-sm">
                    Northern States
                  </span>
                </div>
                
                <div className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-display font-bold text-white mb-1 md:mb-2">
                  ₦<AnimatedCounter
                    target={insights.northTotal / 1e12}
                    duration={2}
                    decimals={2}
                    animate={isActive}
                  />T
                </div>
                
                <div className="flex items-baseline gap-1 md:gap-2">
                  <span className="text-base sm:text-lg md:text-2xl font-bold text-cyan-400">
                    <AnimatedPercentage target={northPercentage} animate={isActive} />
                  </span>
                  <span className="text-xs md:text-sm text-surface-400">of total</span>
                </div>
                
                <div className="mt-2 md:mt-4 pt-2 md:pt-4 border-t border-surface-700">
                  <div className="grid grid-cols-2 gap-2 md:gap-4 text-xs md:text-sm">
                    <div>
                      <div className="text-surface-400">States</div>
                      <div className="text-white font-semibold">{insights.northStatesCount}</div>
                    </div>
                    <div>
                      <div className="text-surface-400">Avg/State</div>
                      <div className="text-white font-semibold">
                        ₦{(insights.northAverage / 1e12).toFixed(2)}T
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* North indicator - hidden on mobile */}
              <div className="hidden lg:block text-center">
                <div className="inline-flex items-center gap-2 text-cyan-400">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l8 18H4L12 2z" />
                  </svg>
                  <span className="font-medium">N</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* South stats - positioned in separate column on desktop */}
          <div className="hidden lg:block space-y-4">
            <div ref={southRef}>
              <div className="p-6 bg-amber-500/10 rounded-2xl border border-amber-500/30">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-amber-400" />
                  <span className="text-amber-400 font-medium uppercase tracking-wide text-sm">
                    Southern States
                  </span>
                </div>
                
                <div className="text-4xl md:text-5xl font-display font-bold text-white mb-2">
                  ₦<AnimatedCounter
                    target={insights.southTotal / 1e12}
                    duration={2}
                    decimals={2}
                    animate={isActive}
                  />T
                </div>
                
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-amber-400">
                    <AnimatedPercentage target={southPercentage} animate={isActive} />
                  </span>
                  <span className="text-surface-400">of total</span>
                </div>
                
                <div className="mt-4 pt-4 border-t border-surface-700">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-surface-400">States</div>
                      <div className="text-white font-semibold">{insights.southStatesCount}</div>
                    </div>
                    <div>
                      <div className="text-surface-400">Avg. per State</div>
                      <div className="text-white font-semibold">
                        ₦{(insights.southAverage / 1e12).toFixed(2)}T
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* South indicator */}
              <div className="text-center mt-4">
                <div className="inline-flex items-center gap-2 text-amber-400">
                  <span className="font-medium">S</span>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 22l-8-18h16L12 22z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          
          {/* South stats - mobile only (side by side with North) */}
          <div ref={southRef} className="lg:hidden space-y-2 md:space-y-4">
            <div className="p-3 sm:p-4 md:p-6 bg-amber-500/10 rounded-xl md:rounded-2xl border border-amber-500/30">
              <div className="flex items-center gap-1.5 md:gap-2 mb-2 md:mb-4">
                <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-amber-400" />
                <span className="text-amber-400 font-medium uppercase tracking-wide text-xs md:text-sm">
                  Southern States
                </span>
              </div>
              
              <div className="text-xl sm:text-2xl md:text-4xl font-display font-bold text-white mb-1 md:mb-2">
                ₦<AnimatedCounter
                  target={insights.southTotal / 1e12}
                  duration={2}
                  decimals={2}
                  animate={isActive}
                />T
              </div>
              
              <div className="flex items-baseline gap-1 md:gap-2">
                <span className="text-base sm:text-lg md:text-2xl font-bold text-amber-400">
                  <AnimatedPercentage target={southPercentage} animate={isActive} />
                </span>
                <span className="text-xs md:text-sm text-surface-400">of total</span>
              </div>
              
              <div className="mt-2 md:mt-4 pt-2 md:pt-4 border-t border-surface-700">
                <div className="grid grid-cols-2 gap-2 md:gap-4 text-xs md:text-sm">
                  <div>
                    <div className="text-surface-400">States</div>
                    <div className="text-white font-semibold">{insights.southStatesCount}</div>
                  </div>
                  <div>
                    <div className="text-surface-400">Avg/State</div>
                    <div className="text-white font-semibold">
                      ₦{(insights.southAverage / 1e12).toFixed(2)}T
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Comparison insight */}
        <div ref={comparisonRef} className="mt-12">
          <div className="p-6 bg-surface-800/50 backdrop-blur-sm rounded-2xl border border-surface-700 text-center">
            <p className="text-lg md:text-xl text-surface-300">
              Southern states receive{' '}
              <span className="text-amber-400 font-bold">
                ₦{((insights.southTotal - insights.northTotal) / 1e12).toFixed(2)} Trillion
              </span>{' '}
              more in total allocations, but with{' '}
              <span className="text-cyan-400 font-bold">
                {insights.northStatesCount - insights.southStatesCount}
              </span>{' '}
              fewer states, the average per northern state is actually{' '}
              <span className={insights.northAverage > insights.southAverage ? 'text-cyan-400' : 'text-amber-400'}>
                {insights.northAverage > insights.southAverage ? 'higher' : 'lower'}
              </span>.
            </p>
            
            {/* Comparison bar */}
            <div className="mt-6 max-w-md mx-auto">
              <div className="flex h-4 rounded-full overflow-hidden bg-surface-700">
                <div 
                  className="bg-gradient-to-r from-cyan-500 to-cyan-400 transition-all duration-1000"
                  style={{ width: `${northPercentage}%` }}
                />
                <div 
                  className="bg-gradient-to-r from-amber-400 to-amber-500 transition-all duration-1000"
                  style={{ width: `${southPercentage}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-sm text-surface-400">
                <span>North ({northPercentage.toFixed(1)}%)</span>
                <span>South ({southPercentage.toFixed(1)}%)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Scene4NorthSouth;

